/**
 * Created by Juno Peng on 12/6/2023.
 * Description:
 * Ag-grid edit/delete button
  * ------ maintenance history ------
  * history:
  * Created by Alan Yang on 5/08/2019.
  * 06/16/2019 Bowen Li - add tooltip for smart-icon
  * 07/03/2019 Ella Ma - modify dashboard router query params
 */

import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { Router } from '@angular/router';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ManageScrerensActionService } from './manage-screen.service';
import { HomeScreenActionTypes } from '../../redux/actions/home-screen.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../redux';
import { SystemActionTypes } from '../../tamalelibs/redux/actions/system.actions';
import { ScreenService } from '../../tamalelibs/services/screen.service';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';
import { SaveScreenDialogService } from '../save-screen-dialog/save-screen-dialog.service';
import { SaveScreenDialogOperations, SaveScreenDialogType, SaveScreenDialogInvoker } from '../save-screen-dialog/save-screen-dialog.view-model';
import { SavedScreen } from '../../tamalelibs/models/saved-screen.model';
import { businessConstants } from '../../constants/business.constants';

@Component({
    selector: 'tam-manage-screens-action-button',
    template: `<div kendoTooltip position="bottom" class='button-group'>
        <smart-icon #anchor name='pencil' class='smart-icon light' (click)='editClicked($event)' title="Edit"></smart-icon>
        <smart-icon #anchor name='trash' class='smart-icon light' (click)='deleteClicked($event)' title="Delete"></smart-icon>
        </div>`,
    styleUrls: ['./manage-screens.component.scss']
})

export class ManageScreensActionButtonComponent implements ICellRendererAngularComp {
    private _params: any;
    constructor(
        private _alertWindow: AlertWindowService,
        private _manageScrerensActionService: ManageScrerensActionService,
        private _screenService: ScreenService,
        private _store: Store<AppState>,
        private _saveScreenDialogService: SaveScreenDialogService,
        private _toast: ToastService,
        private _router: Router,
    ) { }

    agInit(params: any): void {
        this._params = params;
    }

    refresh(): boolean {
        return false;
    }

    // Delete Button
    deleteClicked(cell: any): void {
        const type = this._params.data.type.toLowerCase();
        let message = `Are you sure you want to delete ${this._params.data.name}?`;
        if (type === 'workflow') {
            message = `${message} Everything includes projects, tasks and metadata in this workflow will be deleted.`;
        }
        const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.delete_confirmation_required'),
            [message], 'Delete', 'Cancel')
            .subscribe((result: boolean) => {
                if (result) {
                    this._screenService.deleteById(this._params.data.id).subscribe(() => {
                        this._toast.notify({
                            message: this._params.data.name + ' successfully deleted.',
                            style: NotificationStyles.Success
                        });
                        // reload the homescreen tabs before navigate to home
                        this._store.dispatch({
                            type: HomeScreenActionTypes.LOAD
                        });
                    });

                    // update manage screens
                    this._manageScrerensActionService.removeItem$.next(this._params.data.id);

                    // update left dashboard menu
                    this._store.dispatch({
                        type: SystemActionTypes.DELETE_SCREEN,
                        payload: {
                            id: this._params.data.id,
                        },
                    });
                }
                subscription.unsubscribe();
            });
    }

    // Edit Button
    editClicked(cell: any): void {
        // Types only have "Research", "Dashboard","Intralinks"
        // Type of All Notes/My Notes is "Research", their Author property is "system"
        const type = this._params.data.type.toLowerCase();
        if (type === businessConstants.screenType.dashboard) {
            this._router.navigate(['dashboard/edit', this._params.data.id], {
                queryParams: {
                    from: 'settings'
                }
            });
        } else if (type === businessConstants.screenType.workflow) {
            if (this._params.data.configuration.draft && !this._params.data.configuration.id) {
                this._router.navigate(['workflow/config/draft', this._params.data.id]);
            } else {
                this._router.navigate(['workflow/config/edit', this._params.data.id]);
            }
        } else if (type === businessConstants.screenType.intralink) {
            this._router.navigate(['/feeds/intralinks/', this._params.data.id]);
        } else {
            const screenData = new SavedScreen();
            screenData.id = this._params.data.id;
            screenData.name = this._params.data.name;
            screenData.description = this._params.data.description;
            screenData.isPublic = this._params.data.isPublic;
            screenData.accessList = this._params.data.accessList;
            this._saveScreenDialogService.open({
                operation: SaveScreenDialogOperations.EDIT,
                type: SaveScreenDialogType.RESEARCH_VIEW,
                data: screenData,
                invoker: SaveScreenDialogInvoker.MANAGE_SCREENS
            });
        }
    }
}
